import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, UserAnalyticsDashboard } from '../utils/api';
import {
  UsersIcon,
  UserPlusIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DevicePhoneMobileIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

const UserAnalytics: React.FC = () => {
  const { data: analytics, isLoading, error } = useQuery<UserAnalyticsDashboard>({
    queryKey: ['user-analytics-dashboard'],
    queryFn: async () => {
      const response = await adminApi.getUserAnalyticsDashboard();
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <span className="text-red-700">Failed to load analytics</span>
      </div>
    );
  }

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    trend?: number;
    subtitle?: string;
  }> = ({ title, value, icon, trend, subtitle }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="ml-4 flex-shrink-0">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            {icon}
          </div>
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-4 flex items-center">
          {trend >= 0 ? (
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
          ) : (
            <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
          )}
          <span className={`text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {Math.abs(trend).toFixed(1)}%
          </span>
          <span className="text-sm text-gray-500 ml-1">vs last month</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            User Analytics
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Comprehensive insights into user activity and growth
          </p>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={analytics.total_users.toLocaleString()}
          icon={<UsersIcon className="h-6 w-6 text-blue-600" />}
          trend={analytics.growth_rate_percent}
        />
        <StatCard
          title="Active Users"
          value={analytics.active_users.toLocaleString()}
          icon={<UserGroupIcon className="h-6 w-6 text-green-600" />}
          subtitle={`${((analytics.active_users / analytics.total_users) * 100).toFixed(1)}% of total`}
        />
        <StatCard
          title="Inactive Users"
          value={analytics.inactive_users.toLocaleString()}
          icon={<UsersIcon className="h-6 w-6 text-gray-600" />}
        />
        <StatCard
          title="Unverified"
          value={analytics.unverified_users.toLocaleString()}
          icon={<UserPlusIcon className="h-6 w-6 text-yellow-600" />}
        />
      </div>

      {/* Registration Growth */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Growth</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Today</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {analytics.users_registered_today}
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">This Week</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {analytics.users_registered_this_week}
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">This Month</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {analytics.users_registered_this_month}
            </p>
          </div>
        </div>

        {/* Registration Trend Chart */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">30-Day Trend</h4>
          <div className="flex items-end justify-between h-32 space-x-1">
            {analytics.registration_trend.map((day, index) => {
              const maxCount = Math.max(...analytics.registration_trend.map(d => d.count));
              const heightPercent = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
              return (
                <div
                  key={index}
                  className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer relative group"
                  style={{ height: `${heightPercent}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                  title={`${day.date}: ${day.count} users`}
                >
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                    {day.date}: {day.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Activity Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Activity</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active (Last 7 Days)</span>
              <span className="text-lg font-bold text-gray-900">
                {analytics.users_active_last_7_days.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active (Last 30 Days)</span>
              <span className="text-lg font-bold text-gray-900">
                {analytics.users_active_last_30_days.toLocaleString()}
              </span>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-2">Activity Rate (30d)</div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${((analytics.users_active_last_30_days / analytics.total_users) * 100).toFixed(1)}%`,
                  }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {((analytics.users_active_last_30_days / analytics.total_users) * 100).toFixed(1)}% of total users
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <DevicePhoneMobileIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">Users with Devices</span>
              </div>
              <span className="text-lg font-bold text-gray-900">
                {analytics.users_with_devices.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">Users with Sessions</span>
              </div>
              <span className="text-lg font-bold text-gray-900">
                {analytics.users_with_sessions.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <LinkIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">Users with Friends</span>
              </div>
              <span className="text-lg font-bold text-gray-900">
                {analytics.users_with_friends.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Provider Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Authentication Providers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analytics.auth_providers).map(([provider, count]) => (
            <div key={provider} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 capitalize">{provider}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{count.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                {((count / analytics.total_users) * 100).toFixed(1)}%
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;
